const ORIENTATIONS = new Set(['portrait', 'landscape', 'reverse-landscape']);
const COLOUR_TYPES = new Set(['default', 'color', 'grayscale', 'blackwhite']);
const DUPLEX_MODES = new Set(['one-sided', 'long-edge', 'short-edge', 'tumble']);
const ROTATIONS = new Set([0, 90, 180, 270]);

export class QzPrintError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'QzPrintError';
    this.code = details.code || 'QZ_PRINT_FAILED';
    this.phase = details.phase || 'unknown';
    this.submissionStarted = details.submissionStarted === true;
    this.submitted = details.submitted || [];
    this.uncertain = details.uncertain || [];
    this.pending = details.pending || [];
    this.cause = details.cause;
  }
}

export const normalizePrintInteger = (value, fallback = 1, max = 99) => {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) ? Math.max(1, Math.min(max, parsed)) : fallback;
};

const finiteBetween = (value, minimum, maximum, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum
    ? parsed
    : fallback;
};

export function sanitizeQzProfileSettings(value, defaults) {
  const input = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const warnings = [];
  const repair = (condition, message) => {
    if (condition) warnings.push(message);
  };
  const sizeInput =
    input.size && typeof input.size === 'object' && !Array.isArray(input.size)
      ? input.size
      : {};
  const width = finiteBetween(sizeInput.width, 20, 500, defaults.size.width);
  const height = finiteBetween(sizeInput.height, 20, 500, defaults.size.height);
  const orientation = ORIENTATIONS.has(input.orientation)
    ? input.orientation
    : defaults.orientation;
  const rotation = ROTATIONS.has(Number(input.rotation))
    ? Number(input.rotation)
    : ROTATIONS.has(Number(defaults.rotation))
      ? Number(defaults.rotation)
      : 0;
  const colorType = COLOUR_TYPES.has(input.colorType) ? input.colorType : 'default';
  const duplex = DUPLEX_MODES.has(input.duplex) ? input.duplex : 'one-sided';
  const density =
    input.density === 'best'
      ? 'best'
      : finiteBetween(input.density, 1, 4800, defaults.density);
  repair(sizeInput.width != null && width !== Number(sizeInput.width), 'Invalid paper width was reset.');
  repair(sizeInput.height != null && height !== Number(sizeInput.height), 'Invalid paper height was reset.');
  repair(input.orientation != null && orientation !== input.orientation, 'Invalid orientation was reset.');
  repair(input.rotation != null && rotation !== Number(input.rotation), 'Invalid rotation was reset.');
  repair(input.colorType != null && colorType !== input.colorType, 'Invalid colour mode was reset.');
  repair(input.duplex != null && duplex !== input.duplex, 'Invalid sides setting was reset.');
  repair(input.density != null && density !== input.density, 'Invalid density was reset.');
  return {
    value: {
      ...defaults,
      printer: String(input.printer || defaults.printer).trim(),
      printerTray: String(input.printerTray ?? defaults.printerTray).trim(),
      paperSizeName: String(input.paperSizeName || defaults.paperSizeName).trim(),
      size: { width, height },
      orientation,
      rotation,
      density,
      colorType,
      duplex,
      margins: finiteBetween(input.margins, 0, 30, defaults.margins),
    },
    warnings,
  };
}

export function diagnoseQzProfile(profileKey, settings, printerDetails = []) {
  const warnings = [];
  const printer = printerDetails.find((item) => item.name === settings.printer);
  if (!printer) {
    warnings.push(`Configured printer "${settings.printer}" is not currently available.`);
    return { profileKey, status: 'warning', printer: null, warnings };
  }
  if (!printer.capabilitiesAvailable) {
    warnings.push('Detailed printer capabilities are unavailable; driver defaults may be used.');
  } else {
    if (settings.printerTray && !printer.trays.includes(settings.printerTray)) {
      warnings.push(`Paper source "${settings.printerTray}" was not reported by this printer.`);
    }
    if (
      settings.paperSizeName &&
      !printer.sizes.some((size) => size.name === settings.paperSizeName)
    ) {
      warnings.push(`Paper size "${settings.paperSizeName}" was not reported by this printer.`);
    }
    if (
      settings.density !== 'best' &&
      printer.densities.length &&
      !printer.densities.includes(Number(settings.density))
    ) {
      warnings.push(`${settings.density} dpi was not reported by this printer.`);
    }
  }
  return {
    profileKey,
    status: warnings.length ? 'warning' : 'ready',
    printer,
    warnings,
  };
}

export function validateHtmlPrintJobs(jobs, knownProfiles) {
  if (!Array.isArray(jobs) || !jobs.length) {
    throw new QzPrintError('The print queue is empty.', {
      code: 'QZ_EMPTY_QUEUE',
      phase: 'preflight',
    });
  }
  let totalBytes = 0;
  return jobs.map((job, index) => {
    if (!job || !knownProfiles[job.profileKey]) {
      throw new QzPrintError(`Print job ${index + 1} has an unknown profile.`, {
        code: 'QZ_INVALID_PROFILE',
        phase: 'preflight',
      });
    }
    if (typeof job.html !== 'string' || !job.html.trim()) {
      throw new QzPrintError(`Print job ${index + 1} has no printable HTML.`, {
        code: 'QZ_INVALID_HTML',
        phase: 'preflight',
      });
    }
    totalBytes += new Blob([job.html]).size;
    return {
      ...job,
      id: String(job.id || `qz-${Date.now()}-${index + 1}`),
      sequence: index + 1,
      label: String(job.label || knownProfiles[job.profileKey].label),
    };
  }).map((job) => ({ ...job, totalPayloadBytes: totalBytes }));
}

export function parsePdfPageRanges(value, pageCount) {
  const text = String(value || '').trim();
  if (!text) return { text: '', pages: Array.from({ length: pageCount }, (_, index) => index + 1) };
  const pages = new Set();
  text.split(',').forEach((part) => {
    const match = part.trim().match(/^(\d+)(?:-(\d+))?$/);
    if (!match) throw new Error(`Invalid page range "${part.trim()}". Use values such as 1-4,6.`);
    const start = Number(match[1]);
    const finish = Number(match[2] || match[1]);
    if (start < 1 || finish < start || finish > pageCount) {
      throw new Error(`Page range "${part.trim()}" is outside this ${pageCount}-page PDF.`);
    }
    for (let page = start; page <= finish; page += 1) pages.add(page);
  });
  return { text, pages: Array.from(pages).sort((a, b) => a - b) };
}

export function validatePdfBase64(value) {
  const data = String(value || '').replace(/^data:application\/pdf;base64,/i, '').trim();
  if (!data || !/^[A-Za-z0-9+/]+={0,2}$/.test(data)) {
    throw new QzPrintError('The PDF data is missing or invalid.', {
      code: 'QZ_INVALID_PDF',
      phase: 'preflight',
    });
  }
  try {
    const prefix =
      typeof atob === 'function'
        ? atob(data.slice(0, 16))
        : Buffer.from(data.slice(0, 16), 'base64').toString('binary');
    if (!prefix.startsWith('%PDF-')) throw new Error('Not a PDF');
  } catch {
    throw new QzPrintError('The selected data is not a valid PDF document.', {
      code: 'QZ_INVALID_PDF',
      phase: 'preflight',
    });
  }
  return data;
}

export function withPrintTimeout(promise, timeoutMs, message, details = {}) {
  let timer;
  return Promise.race([
    Promise.resolve(promise),
    new Promise((_, reject) => {
      timer = setTimeout(
        () =>
          reject(
            new QzPrintError(message, {
              code: details.code || 'QZ_TIMEOUT',
              phase: details.phase || 'connection',
              submissionStarted: details.submissionStarted,
            }),
          ),
        timeoutMs,
      );
    }),
  ]).finally(() => clearTimeout(timer));
}
